import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  ShieldCheck, Clock, Award, AlertTriangle, CheckCircle2, XCircle,
  PlayCircle, FileBadge2, Download, RefreshCw, Lock, ChevronRight, Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

export default function EsameFinaleSection({ user, hasCorsoAccess }) {
  const [stato, setStato] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("intro"); // intro | quiz | result
  const [tentativo, setTentativo] = useState(null);
  const [risposte, setRisposte] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [confirmStart, setConfirmStart] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const timerRef = useRef(null);

  const refreshStato = useCallback(async () => {
    if (!hasCorsoAccess && user?.role !== "admin") {
      setLoading(false);
      return;
    }
    try {
      const r = await axios.get(`${API}/api/me/esame-finale/stato`, { withCredentials: true });
      setStato(r.data);
    } catch {
      setStato(null);
    } finally {
      setLoading(false);
    }
  }, [hasCorsoAccess, user]);

  useEffect(() => { refreshStato(); }, [refreshStato]);

  // Timer
  useEffect(() => {
    if (view !== "quiz" || !tentativo?.scadenza) return;
    const tick = () => {
      const ms = new Date(tentativo.scadenza).getTime() - Date.now();
      setTimeLeft(Math.max(0, Math.floor(ms / 1000)));
      if (ms <= 0) {
        clearInterval(timerRef.current);
        autoSubmit();
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, tentativo]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const ss = s % 60;
    return `${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  };

  const handleStart = async () => {
    setConfirmStart(false);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/me/esame-finale/start`, {}, { withCredentials: true });
      setTentativo(r.data);
      setRisposte({});
      setCurrentIdx(0);
      setView("quiz");
    } catch (e) {
      alert(e.response?.data?.detail || "Impossibile avviare l'esame");
    } finally {
      setLoading(false);
    }
  };

  const autoSubmit = async () => {
    if (!tentativo || submitting) return;
    await handleSubmit(true);
  };

  const handleSubmit = async (auto = false) => {
    if (submitting) return;
    setSubmitting(true);
    setConfirmSubmit(false);
    try {
      const payload = {
        tentativo_id: tentativo.tentativo_id,
        risposte: tentativo.domande.map((d) => (risposte[d.id] !== undefined ? risposte[d.id] : -1)),
      };
      const r = await axios.post(`${API}/api/me/esame-finale/submit`, payload, { withCredentials: true });
      setResult({ ...r.data, autoSubmit: auto });
      setView("result");
      clearInterval(timerRef.current);
      await refreshStato();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore invio esame");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewAttempt = () => {
    setResult(null);
    setTentativo(null);
    setView("intro");
  };

  const downloadCertificato = () => {
    window.open(`${API}/api/me/esame-finale/certificato.pdf`, "_blank");
  };

  // ----- RENDER -----
  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 flex items-center justify-center gap-3 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin" /> Caricamento…
      </div>
    );
  }

  if (!stato || !stato.accesso) {
    return (
      <div data-testid="esame-blocco-accesso" className="rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-2xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
            <Lock className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-extrabold text-[#0a1f55] text-lg">Esame Finale non disponibile</h3>
            <p className="text-[13px] text-slate-600 mt-1">
              {stato?.motivo_blocco || "Solo i soci che hanno acquistato il Corso A.C.D.T. per la qualificazione professionale possono sostenere l'esame finale."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ----- VISTA RISULTATO -----
  if (view === "result" && result) {
    return (
      <ResultView
        result={result}
        onNew={handleNewAttempt}
        onDownload={downloadCertificato}
        tentativi_residui={result.tentativi_residui}
        maxTent={stato.max_tentativi}
      />
    );
  }

  // ----- VISTA QUIZ -----
  if (view === "quiz" && tentativo) {
    const d = tentativo.domande[currentIdx];
    const answered = Object.keys(risposte).length;
    const progress = Math.round((answered / tentativo.domande.length) * 100);
    const lowTime = timeLeft <= 300; // <5 min
    return (
      <div data-testid="esame-quiz-view" className="space-y-4">
        {/* HEADER esame finale */}
        <div className="rounded-2xl border-2 border-[#0a1f55] bg-white shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-[#0a1f55] to-[#102a6b] text-white px-6 py-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.18em] font-extrabold text-[#fbbf24]">
                <ShieldCheck className="h-3.5 w-3.5" /> Esame Finale — Tentativo {tentativo.numero_tentativo}/{stato.max_tentativi}
              </div>
              <h3 className="font-extrabold text-base mt-1">Corso A.C.D.T. per la qualificazione professionale</h3>
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-xl font-extrabold text-lg ${lowTime ? "bg-red-500 animate-pulse" : "bg-white/15"}`}>
              <Clock className="h-5 w-5" />
              <span data-testid="esame-timer">{formatTime(timeLeft)}</span>
            </div>
          </div>
          <div className="h-1.5 bg-slate-200 relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-[#f59e0b] transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* DOMANDA */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-slate-100 text-[10.5px] uppercase tracking-wider font-extrabold text-slate-600">
                {d.categoria}
              </div>
              <h4 className="font-extrabold text-[#0a1f55] text-lg mt-2">
                Domanda {d.numero} di {tentativo.domande.length}
              </h4>
            </div>
            <div className="text-[12px] text-slate-500">
              Risposte date: <span className="font-bold text-[#0a1f55]">{answered}/{tentativo.domande.length}</span>
            </div>
          </div>

          <p data-testid="esame-domanda-testo" className="text-[15px] text-slate-800 leading-relaxed mb-5">{d.testo}</p>

          <div className="space-y-2.5">
            {d.opzioni.map((opt, idx) => {
              const sel = risposte[d.id] === idx;
              return (
                <button
                  key={idx}
                  type="button"
                  data-testid={`opt-${idx}`}
                  onClick={() => setRisposte((r) => ({ ...r, [d.id]: idx }))}
                  className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all flex items-start gap-3 ${
                    sel
                      ? "border-[#0a1f55] bg-[#0a1f55]/5 shadow"
                      : "border-slate-200 hover:border-[#102a6b]/40 bg-white"
                  }`}
                >
                  <div className={`h-7 w-7 rounded-lg font-extrabold text-[12px] flex items-center justify-center flex-shrink-0 ${
                    sel ? "bg-[#0a1f55] text-white" : "bg-slate-100 text-slate-600"
                  }`}>
                    {String.fromCharCode(65 + idx)}
                  </div>
                  <span className={`text-[13.5px] leading-relaxed pt-1 ${sel ? "font-bold text-[#0a1f55]" : "text-slate-700"}`}>{opt}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* NAVIGAZIONE */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center justify-between gap-3 flex-wrap">
          <Button
            variant="outline"
            disabled={currentIdx === 0}
            onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
            data-testid="esame-prev"
          >
            ← Precedente
          </Button>
          <div className="flex flex-wrap gap-1 max-w-md justify-center">
            {tentativo.domande.map((q, i) => {
              const isCurrent = i === currentIdx;
              const isAnswered = risposte[q.id] !== undefined;
              return (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => setCurrentIdx(i)}
                  data-testid={`q-jump-${i + 1}`}
                  className={`h-7 w-7 rounded-md text-[10.5px] font-extrabold transition-colors ${
                    isCurrent
                      ? "bg-[#f59e0b] text-[#0a1020]"
                      : isAnswered
                      ? "bg-[#0a1f55] text-white"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                  title={`Domanda ${i + 1}${isAnswered ? " (risposta)" : ""}`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
          {currentIdx < tentativo.domande.length - 1 ? (
            <Button
              onClick={() => setCurrentIdx((i) => Math.min(tentativo.domande.length - 1, i + 1))}
              data-testid="esame-next"
              className="bg-[#0a1f55] hover:bg-[#102a6b]"
            >
              Successiva →
            </Button>
          ) : (
            <Button
              onClick={() => setConfirmSubmit(true)}
              data-testid="esame-submit"
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={submitting}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Termina esame"} <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>

        {/* MODALE CONFERMA SUBMIT */}
        {confirmSubmit && (
          <ConfirmModal
            title="Confermi l'invio dell'esame?"
            message={`Hai risposto a ${answered} domande su ${tentativo.domande.length}. Una volta inviato non potrai modificare le risposte.`}
            confirmLabel="Invia esame"
            confirmClass="bg-emerald-600 hover:bg-emerald-700"
            onConfirm={() => handleSubmit(false)}
            onCancel={() => setConfirmSubmit(false)}
            testIdPrefix="confirm-submit"
          />
        )}
      </div>
    );
  }

  // ----- VISTA INTRO -----
  const tentativi_residui = stato.tentativi_residui;
  const noMoreAttempts = tentativi_residui <= 0 && !stato.superato;

  return (
    <div data-testid="esame-intro" className="space-y-5">
      {/* HERO */}
      <div className="rounded-2xl border-2 border-[#0a1f55] bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white p-7 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-[#f59e0b]/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f59e0b]/20 text-[#fbbf24] text-[11px] font-extrabold uppercase tracking-[0.18em] mb-3">
            <ShieldCheck className="h-3.5 w-3.5" /> Esame Finale
          </div>
          <h2 className="text-2xl md:text-3xl font-extrabold leading-tight">Esame di Qualificazione Professionale</h2>
          <p className="text-[13.5px] text-slate-200 mt-2 max-w-2xl">
            L&apos;esame finale del Corso A.C.D.T. per la qualificazione professionale attesta la tua preparazione come Consulente Digitale Tecnologico. Il superamento sblocca il <strong>Certificato di Qualificazione</strong>.
          </p>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
            <InfoBox label="Domande" value="30" />
            <InfoBox label="Durata" value="60 min" />
            <InfoBox label="Soglia" value="70%" />
            <InfoBox label="Tentativi" value={`${stato.max_tentativi} max`} />
          </div>
        </div>
      </div>

      {/* STATO UTENTE */}
      {stato.superato ? (
        <div data-testid="esame-superato" className="rounded-2xl border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 to-white p-6">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="h-14 w-14 rounded-2xl bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
              <Award className="h-7 w-7" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.18em] font-extrabold text-emerald-700">Esame superato</div>
              <h3 className="text-lg font-extrabold text-[#0a1f55]">Hai conseguito la Qualificazione Professionale A.C.D.T.</h3>
              <p className="text-[13px] text-slate-600 mt-1">
                Miglior punteggio: <strong>{stato.miglior_punteggio}%</strong> · Data superamento: <strong>{new Date(stato.data_superamento).toLocaleDateString("it-IT")}</strong>
              </p>
            </div>
            <Button
              onClick={downloadCertificato}
              data-testid="esame-download-certificato"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold"
            >
              <Download className="h-4 w-4 mr-2" /> Scarica Certificato PDF
            </Button>
          </div>
        </div>
      ) : noMoreAttempts ? (
        <UnlockBlock
          stato={stato}
          onRefresh={refreshStato}
        />
      ) : (
        <div data-testid="esame-pronto" className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white p-6">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="h-12 w-12 rounded-2xl bg-[#f59e0b] text-white flex items-center justify-center flex-shrink-0">
              <PlayCircle className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-extrabold text-[#0a1f55] text-lg">Sei pronto a sostenere l&apos;esame?</h3>
              <p className="text-[13px] text-slate-600 mt-1">
                Tentativi residui: <strong>{tentativi_residui} di {stato.max_tentativi}</strong>. Una volta avviato il timer parte automaticamente.
              </p>
            </div>
            <Button
              onClick={() => setConfirmStart(true)}
              data-testid="esame-start-btn"
              className="bg-[#0a1f55] hover:bg-[#102a6b] text-white font-extrabold h-11 px-6"
            >
              <PlayCircle className="h-4 w-4 mr-2" /> Inizia l&apos;esame
            </Button>
          </div>
        </div>
      )}

      {/* REGOLAMENTO */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-3">
          <FileBadge2 className="h-5 w-5 text-[#102a6b]" />
          <h3 className="font-extrabold text-[#0a1f55]">Regolamento dell&apos;esame</h3>
        </div>
        <ul className="space-y-2 text-[13px] text-slate-700">
          <Rule>L&apos;esame è composto da <strong>30 domande a risposta multipla</strong> (4 opzioni, 1 sola corretta).</Rule>
          <Rule>Durata massima: <strong>60 minuti</strong>. Al termine del tempo, il sistema invia automaticamente le risposte.</Rule>
          <Rule>Soglia di superamento: <strong>70% di risposte corrette</strong> (almeno 21 su 30).</Rule>
          <Rule>Sono ammessi <strong>massimo 3 tentativi</strong>. Dopo il 3° tentativo, contatta la segreteria.</Rule>
          <Rule>Le domande sono estratte casualmente dal pool ufficiale dei <strong>20 moduli formativi</strong> A.C.D.T.</Rule>
          <Rule>Non è consentito l&apos;ausilio di terzi. L&apos;eventuale superamento sblocca il <strong>Certificato di Qualificazione PDF</strong>.</Rule>
        </ul>
      </div>

      {/* STORICO TENTATIVI */}
      {stato.storico.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="font-extrabold text-[#0a1f55] mb-3">Storico tentativi</h3>
          <div className="space-y-2">
            {stato.storico.map((t) => (
              <div key={t.tentativo_id} data-testid={`storico-${t.numero_tentativo}`} className="rounded-xl border border-slate-200 p-3 flex items-center gap-3 flex-wrap">
                <div className="h-9 w-9 rounded-lg bg-slate-100 text-slate-700 font-extrabold text-[12px] flex items-center justify-center flex-shrink-0">
                  #{t.numero_tentativo}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold text-[#0a1f55]">
                    {t.corrette ?? 0} / {t.totale} corrette ({t.punteggio ?? 0}%)
                  </div>
                  <div className="text-[11.5px] text-slate-500">{new Date(t.data).toLocaleString("it-IT")}</div>
                </div>
                <div className={`px-3 py-1 rounded-full text-[10.5px] font-extrabold uppercase tracking-wider ${
                  t.superato
                    ? "bg-emerald-100 text-emerald-700"
                    : t.stato === "scaduto"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-red-100 text-red-700"
                }`}>
                  {t.superato ? "Superato" : t.stato === "scaduto" ? "Tempo scaduto" : "Non superato"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CONFIRM START MODAL */}
      {confirmStart && (
        <ConfirmModal
          title="Vuoi davvero iniziare l'esame?"
          message={`Una volta avviato, il timer di ${60} minuti parte automaticamente. Questo conta come tentativo ${stato.tentativi_effettuati + 1}/${stato.max_tentativi}.`}
          confirmLabel="Sì, inizia"
          confirmClass="bg-[#0a1f55] hover:bg-[#102a6b]"
          onConfirm={handleStart}
          onCancel={() => setConfirmStart(false)}
          testIdPrefix="confirm-start"
        />
      )}
    </div>
  );
}

// ----- SUB-COMPONENTS -----
function InfoBox({ label, value }) {
  return (
    <div className="rounded-xl bg-white/10 backdrop-blur p-3 border border-white/10">
      <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#fbbf24]">{label}</div>
      <div className="text-xl font-extrabold mt-0.5">{value}</div>
    </div>
  );
}

function Rule({ children }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function ConfirmModal({ title, message, confirmLabel, confirmClass, onConfirm, onCancel, testIdPrefix }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border-2 border-[#0a1f55]" data-testid={`${testIdPrefix}-modal`}>
        <div className="flex items-start gap-3 mb-4">
          <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-extrabold text-[#0a1f55]">{title}</h3>
            <p className="text-[13px] text-slate-600 mt-1">{message}</p>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onCancel} data-testid={`${testIdPrefix}-cancel`}>Annulla</Button>
          <Button onClick={onConfirm} className={`text-white font-extrabold ${confirmClass}`} data-testid={`${testIdPrefix}-confirm`}>{confirmLabel}</Button>
        </div>
      </div>
    </div>
  );
}

function UnlockBlock({ stato, onRefresh }) {
  const [requesting, setRequesting] = useState(false);
  const [stripeUrl, setStripeUrl] = useState(null);

  const handleRichiedi = async () => {
    setRequesting(true);
    try {
      const r = await axios.post(`${API}/api/me/esame-finale/sblocco/richiedi`, {}, { withCredentials: true });
      if (r.data.stripe_url) {
        // Redirect immediato a Stripe Checkout
        window.location.href = r.data.stripe_url;
        return;
      }
      setStripeUrl(r.data.stripe_url);
      await onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore richiesta sblocco");
    } finally {
      setRequesting(false);
    }
  };

  const handlePay = () => {
    if (stripeUrl) {
      window.location.href = stripeUrl;
    } else {
      handleRichiedi();
    }
  };

  if (!stato.sblocco_richiesto) {
    // Step 0: blocco iniziale, mostra opzione "richiedi sblocco"
    return (
      <div data-testid="esame-no-tentativi" className="rounded-2xl border-2 border-red-300 bg-gradient-to-br from-red-50 to-white p-6 space-y-4">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-2xl bg-red-500 text-white flex items-center justify-center flex-shrink-0">
            <XCircle className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-extrabold text-[#0a1f55] text-lg">Hai esaurito i 3 tentativi disponibili</h3>
            <p className="text-[13px] text-slate-600 mt-1">
              Per accedere a un nuovo tentativo è prevista una procedura di <strong>rimodulazione del test</strong> con un contributo integrativo di <strong>{stato.sblocco_prezzo}€</strong>. Il sistema richiederà inoltre di <strong>riaprire tutti i PDF dei moduli</strong> per assicurarsi che tu abbia ripassato la materia.
            </p>
          </div>
        </div>
        <div className="rounded-xl bg-white border border-red-200 p-4">
          <div className="text-[10.5px] uppercase tracking-wider font-extrabold text-red-700 mb-2">Come funziona lo sblocco</div>
          <ol className="space-y-1.5 text-[13px] text-slate-700 list-decimal pl-5">
            <li>Richiedi lo sblocco qui sotto</li>
            <li>Paga i {stato.sblocco_prezzo}€ via Stripe (carta, PayPal o Klarna)</li>
            <li>Riapri tutti i {stato.sblocco_pdf_totali} PDF dei moduli (verifica automatica)</li>
            <li>Quando entrambi i passi sono completati, lo sblocco si attiva automaticamente</li>
          </ol>
        </div>
        <Button
          onClick={handleRichiedi}
          disabled={requesting}
          data-testid="esame-richiedi-sblocco"
          className="bg-[#0a1f55] hover:bg-[#102a6b] text-white font-extrabold h-11 px-6"
        >
          {requesting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Richiedi sblocco esame ({stato.sblocco_prezzo}€)
        </Button>
      </div>
    );
  }

  // Step 1+: richiesta sblocco creata, mostra avanzamento
  const allDone = stato.sblocco_pagamento_ok && stato.sblocco_pdf_ok;
  return (
    <div data-testid="esame-sblocco-progress" className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white p-6 space-y-4">
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-2xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
          <RefreshCw className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h3 className="font-extrabold text-[#0a1f55] text-lg">Sblocco esame in corso</h3>
          <p className="text-[13px] text-slate-600 mt-1">
            Completa entrambi gli step per ricevere automaticamente un nuovo ciclo di tentativi.
          </p>
        </div>
      </div>

      {/* Step 1: Pagamento */}
      <div className={`rounded-xl border-2 p-4 ${stato.sblocco_pagamento_ok ? "border-emerald-300 bg-emerald-50/60" : "border-slate-200 bg-white"}`}>
        <div className="flex items-start gap-3">
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${stato.sblocco_pagamento_ok ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-500"}`}>
            {stato.sblocco_pagamento_ok ? <CheckCircle2 className="h-5 w-5" /> : <span className="font-extrabold">1</span>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-extrabold text-[#0a1f55] text-[14px]">Pagamento {stato.sblocco_prezzo}€</div>
            <div className="text-[12px] text-slate-600">
              {stato.sblocco_pagamento_ok ? "Confermato dalla segreteria" : "Effettua il pagamento PayPal, sarà confermato dalla segreteria entro 24h"}
            </div>
          </div>
          {!stato.sblocco_pagamento_ok && (
            <Button onClick={handlePay} data-testid="esame-pay-sblocco" className="bg-[#0a1f55] hover:bg-[#102a6b] text-white font-extrabold h-9 px-4 text-[12px]">
              Paga {stato.sblocco_prezzo}€ →
            </Button>
          )}
        </div>
      </div>

      {/* Step 2: PDF revisione */}
      <div className={`rounded-xl border-2 p-4 ${stato.sblocco_pdf_ok ? "border-emerald-300 bg-emerald-50/60" : "border-slate-200 bg-white"}`}>
        <div className="flex items-start gap-3">
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${stato.sblocco_pdf_ok ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-500"}`}>
            {stato.sblocco_pdf_ok ? <CheckCircle2 className="h-5 w-5" /> : <span className="font-extrabold">2</span>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-extrabold text-[#0a1f55] text-[14px]">Revisione moduli PDF</div>
            <div className="text-[12px] text-slate-600">
              {stato.sblocco_pdf_ok ? "Tutti i moduli sono stati rivisti" : `Riapri tutti i ${stato.sblocco_pdf_totali} PDF dal blocco "Materiali didattici" sopra`}
            </div>
            {/* Progress bar */}
            <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full bg-amber-500 transition-all"
                style={{ width: `${Math.min(100, (stato.sblocco_pdf_revisionati / Math.max(1, stato.sblocco_pdf_totali)) * 100)}%` }}
              />
            </div>
            <div className="text-[11.5px] text-slate-500 mt-1 font-bold">
              {stato.sblocco_pdf_revisionati} / {stato.sblocco_pdf_totali} moduli rivisti
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" onClick={onRefresh} data-testid="esame-sblocco-refresh" className="h-9 text-[12.5px]">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Aggiorna stato
        </Button>
        {allDone && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-[11.5px] font-extrabold uppercase tracking-wider animate-pulse">
            <CheckCircle2 className="h-3.5 w-3.5" /> Sblocco pronto, attivazione in corso…
          </div>
        )}
      </div>
    </div>
  );
}

function ResultView({ result, onNew, onDownload, tentativi_residui, maxTent }) {
  const [showDettaglio, setShowDettaglio] = useState(false);
  return (
    <div data-testid="esame-result-view" className="space-y-5">
      <div className={`rounded-2xl border-2 p-7 ${result.superato ? "border-emerald-400 bg-gradient-to-br from-emerald-50 to-white" : "border-red-300 bg-gradient-to-br from-red-50 to-white"}`}>
        <div className="flex items-start gap-4 flex-wrap">
          <div className={`h-16 w-16 rounded-2xl flex items-center justify-center flex-shrink-0 text-white ${result.superato ? "bg-emerald-500" : "bg-red-500"}`}>
            {result.superato ? <Award className="h-8 w-8" /> : <XCircle className="h-8 w-8" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-[10.5px] uppercase tracking-[0.18em] font-extrabold ${result.superato ? "text-emerald-700" : "text-red-700"}`}>
              {result.superato ? "Esame Superato" : result.autoSubmit ? "Tempo scaduto" : "Esame non superato"}
            </div>
            <h2 className="text-3xl font-extrabold text-[#0a1f55] mt-1">{result.punteggio}%</h2>
            <p className="text-[13.5px] text-slate-700 mt-1">
              <strong>{result.corrette}</strong> risposte corrette su <strong>{result.totale}</strong> · Soglia: {result.soglia}%
            </p>
            {!result.superato && (
              <p className="text-[12.5px] text-slate-600 mt-2">
                Tentativi residui: <strong>{tentativi_residui}/{maxTent}</strong>
              </p>
            )}
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          {result.superato ? (
            <Button onClick={onDownload} data-testid="result-download-cert" className="bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold">
              <Download className="h-4 w-4 mr-2" /> Scarica Certificato
            </Button>
          ) : tentativi_residui > 0 ? (
            <Button onClick={onNew} data-testid="result-retry" className="bg-[#0a1f55] hover:bg-[#102a6b] text-white">
              <RefreshCw className="h-4 w-4 mr-2" /> Riprova
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => setShowDettaglio((v) => !v)} data-testid="result-toggle-dettaglio">
            {showDettaglio ? "Nascondi" : "Mostra"} dettaglio risposte
          </Button>
        </div>
      </div>

      {showDettaglio && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
          {result.dettaglio.map((d) => (
            <div key={d.id} data-testid={`result-dom-${d.numero}`} className={`rounded-xl border p-4 ${d.corretta ? "border-emerald-200 bg-emerald-50/40" : "border-red-200 bg-red-50/30"}`}>
              <div className="flex items-start gap-2">
                {d.corretta ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" /> : <XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#0a1f55] text-[13px]">D{d.numero}. {d.testo}</div>
                  <div className="mt-2 space-y-1 text-[12.5px]">
                    {d.opzioni.map((opt, i) => {
                      const isCorrect = i === d.risposta_corretta;
                      const isUserAns = i === d.risposta_utente;
                      return (
                        <div key={i} className={`flex items-start gap-2 px-2 py-1 rounded ${
                          isCorrect ? "bg-emerald-100 text-emerald-900 font-bold"
                          : isUserAns ? "bg-red-100 text-red-900"
                          : "text-slate-600"
                        }`}>
                          <span className="font-extrabold w-5">{String.fromCharCode(65 + i)}.</span>
                          <span className="flex-1">{opt}</span>
                          {isCorrect && <span className="text-[10.5px] uppercase tracking-wider font-extrabold">Corretta</span>}
                          {isUserAns && !isCorrect && <span className="text-[10.5px] uppercase tracking-wider font-extrabold">Tua risposta</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
